import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { forwardRef } from 'react';
import type { LabelProps, PopupProps } from 'semantic-ui-react';
import { Label, Popup } from 'semantic-ui-react';

import type { AssetType } from '@features/applications/applications.models';

import { getAssetTypeMetadata } from './asset-type.const';
import s from './asset-type-label.scss';

export interface AssetTypeLabelProps extends Pick<
  LabelProps,
  'className' | 'size'
> {
  /**
   * The asset type to display
   */
  type?: AssetType;
  /**
   * Reduces the label padding for a more compact appearance.
   */
  compact?: boolean;
  /**
   * Props to pass to the Popup component for fine-grained control.
   *
   * @default { position: 'bottom left' }
   * @example { disabled: true } // prevent the popup from showing
   */
  popup?: Partial<PopupProps>;
}

/**
 * A preset label component to show Asset Types with an icon and description in
 * a tooltip on hover.
 *
 * @example
 * ```tsx
 * <AssetTypeLabel type={AssetType.Container} />
 * <AssetTypeLabel type={AssetType.Application} size="large" compact />
 * ```
 */
export const AssetTypeLabel = forwardRef<HTMLDivElement, AssetTypeLabelProps>(
  ({ className, compact, type, popup, ...labelProps }, forwardedRef) => {
    useStyles(s);

    const {
      description,
      icon: Icon,
      shortText,
      text,
    } = getAssetTypeMetadata(type);

    const label = (
      <Label
        ref={forwardedRef}
        className={classNames(s.root, compact && s.compact, className)}
        {...labelProps}
      >
        <Icon />
        {shortText ?? text}
      </Label>
    );

    return (
      <Popup
        role="tooltip"
        position="bottom left"
        content={description}
        mouseEnterDelay={500}
        {...popup}
        trigger={label}
      />
    );
  },
);
AssetTypeLabel.displayName = 'AssetTypeLabel';

export default AssetTypeLabel;
